/* Basketball preview order summaries without a Builder document or canvas.
   Reuse the builder's pure tile, official-geometry, and retail-price helpers.
   Other sports have different line-fit startup paths and deliberately return
   null. This module never changes the source hash or the preview camera. */
(function (global) {
  'use strict';
  if (global.SwishCBPreviewSummaryMath) return;

  function decode(hash) {
    var raw = String(hash || '').replace(/&amp;/g, '&').replace(/^#/, '');
    var marker = raw.indexOf('swishcb=');
    if (marker >= 0) raw = raw.slice(marker + 8);
    return new URLSearchParams(raw);
  }

  function number(value, fallback) {
    if (value == null || value === '') return fallback;
    var parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function enabled(value) { return /^(1|true|yes|on)$/i.test(String(value || '')); }
  function disabled(value) { return /^(0|false|no|off)$/i.test(String(value || '')); }

  function hoopSides(params) {
    var mode = Math.max(0, Math.round(number(params.get('hm'), 0)));
    var explicit = ['hl', 'hr', 'ht', 'hb'].some(function (key) { return params.has(key); });
    var sides = explicit ? {
      left: enabled(params.get('hl')), right: enabled(params.get('hr')),
      top: enabled(params.get('ht')), bottom: enabled(params.get('hb'))
    } : { left: mode > 0, right: mode > 1, top: mode > 2, bottom: mode > 3 };
    // The builder restores a right-only one-hoop design as left-only.
    if (!sides.left && sides.right && !sides.top && !sides.bottom) {
      sides.left = true;
      sides.right = false;
    }
    return sides;
  }

  function normalizeInboundSingleHoopBorderDimensions(params, rules, unit, sides, lengthM, widthM) {
    // Mirror swishcb_normalizeInboundSingleHoopBorderPreview() in the builder.
    // Legacy one-hoop cards used approximate imperial equivalents of the
    // 2m-border preset. The builder restores its 15m depth before tile fitting;
    // pricing the approximate incoming dimensions would underquote that floor.
    var selectedHoops = ['left', 'right', 'top', 'bottom'].reduce(function (count, side) {
      return count + (sides[side] ? 1 : 0);
    }, 0);
    if (number(params.get('hm'), 0) !== 1 && selectedHoops !== 1) return { lengthM: lengthM, widthM: widthM };
    var borderM = ['bsl', 'bsr', 'bst', 'bsb'].reduce(function (largest, key) {
      var raw = number(params.get(key), 0);
      if (!(raw > 0)) return largest;
      var meters = unit === 'ft' ? raw * 0.3048 : (raw > 20 ? raw / 100 : raw);
      return largest == null ? meters : Math.max(largest, meters);
    }, null);
    if (borderM == null || Math.abs(borderM - 2) > 0.35) return { lengthM: lengthM, widthM: widthM };
    var expectedWidthM = Number(rules.playableWidth) + 4;
    var expectedLengthM = 11 + 4;
    if (Math.abs(widthM - expectedWidthM) > 0.45 || lengthM >= expectedLengthM - 0.05) {
      return { lengthM: lengthM, widthM: widthM };
    }
    return { lengthM: expectedLengthM, widthM: expectedWidthM };
  }

  function resolve(hash, countryCode, config) {
    var cfg = config || global.SwishCBConfig;
    var getSpec = global.swishcb_tiles_getSpec;
    var nearest = global.swishcb_tiles_snapToBoundsMeters;
    var contain = global.swishcb_tiles_snapToContainBoundsMeters;
    if (!cfg || !cfg.RULESETS || typeof getSpec !== 'function' || typeof nearest !== 'function' ||
        typeof contain !== 'function') return null;

    var params = decode(hash);
    var court = String(params.get('ct') || '').toLowerCase();
    var rulesId = (cfg.COURT_TYPE_TO_RULESET || {})[court] || court.toUpperCase();
    var rules = cfg.RULESETS[rulesId];
    if (!rules) return null;
    var basketball = cfg.HOOP_MARKER_RULES && cfg.HOOP_MARKER_RULES.has(rulesId);
    if (!basketball) return null;
    var unit = params.get('u') === 'ft' ? 'ft' : 'm';
    var factor = unit === 'ft' ? 0.3048 : 1;
    var requestedLengthM = number(params.get('l'), 0) * factor;
    var requestedWidthM = number(params.get('w'), 0) * factor;
    if (!(requestedLengthM > 0) || !(requestedWidthM > 0)) return null;
    var series = global.swishcb_tiles_normalizeSeriesId(params.get('ts') || 'elastic');
    var spec = getSpec(series);
    var tiled = !enabled(params.get('hw')) && !disabled(params.get('t')) && params.get('ts') !== 'none';
    var sides = hoopSides(params);
    var restoredDimensions = normalizeInboundSingleHoopBorderDimensions(params, rules, unit, sides, requestedLengthM, requestedWidthM);
    requestedLengthM = restoredDimensions.lengthM;
    requestedWidthM = restoredDimensions.widthM;
    var officialLengthM = Number(rules.playableLength);
    var officialWidthM = Number(rules.playableWidth);
    var singleLengthHoop = basketball && sides.left !== sides.right && !sides.top && !sides.bottom;
    // A one-hoop basketball preset uses the same 11m/36ft official depth as
    // getRawBorderOfficialPlayableDimensionMeters() in the builder.
    if (singleLengthHoop) officialLengthM = unit === 'ft' ? 36 * 0.3048 : 11;
    var minLengthM = number(cfg.LIMITS && cfg.LIMITS.m && cfg.LIMITS.m.L[0], 0.01);
    var minWidthM = number(cfg.LIMITS && cfg.LIMITS.m && cfg.LIMITS.m.W[0], 0.01);
    var length = { totalM: requestedLengthM, tiles: null };
    var width = { totalM: requestedWidthM, tiles: null };
    if (tiled) {
      length = nearest(requestedLengthM, minLengthM, Infinity, series);
      width = nearest(requestedWidthM, minWidthM, Infinity, series);
      if (requestedLengthM + 1e-6 >= officialLengthM && length.totalM + 1e-6 < officialLengthM) {
        length = contain(officialLengthM, minLengthM, Infinity, series);
      }
      if (requestedWidthM + 1e-6 >= officialWidthM && width.totalM + 1e-6 < officialWidthM) {
        width = contain(officialWidthM, minWidthM, Infinity, series);
      }
    }

    var edgeMask = tiled ? Math.max(0, Math.min(15, Math.round(number(params.get('te'), 15)))) : 0;
    var edgeM = tiled ? Number(spec.edgeM) || 0 : 0;
    var installedLengthM = length.totalM + edgeM * ((edgeMask & 1 ? 1 : 0) + (edgeMask & 2 ? 1 : 0));
    var installedWidthM = width.totalM + edgeM * ((edgeMask & 4 ? 1 : 0) + (edgeMask & 8 ? 1 : 0));
    var result = {
      unit: unit, series: series, tiled: tiled, courtType: court,
      footprintLengthM: length.totalM, footprintWidthM: width.totalM,
      installedLengthM: installedLengthM, installedWidthM: installedWidthM,
      installedAreaM2: installedLengthM * installedWidthM,
      lengthTiles: length.tiles, widthTiles: width.tiles, edgeMask: edgeMask,
      keyWidthM: null, keyLengthM: null, threePointM: null, threePointVisible: false, overhangM: null,
      pricing: null
    };

    if (basketball && typeof cfg.resolveOfficialCourtGeometry === 'function') {
      var preset = typeof cfg.resolveHoopPresetGeometry === 'function'
        ? cfg.resolveHoopPresetGeometry(params.get('hp') || 'CV72') : null;
      var baseOverhangM = Number(rules.geometry && rules.geometry.hoopToEnd) || 0;
      var defaultOverhangM = Number(preset && preset.manufacturerOverhangM) || baseOverhangM;
      var primarySide = ['left', 'right', 'top', 'bottom'].find(function (side) { return sides[side]; });
      var geometryOptions = { surfaceLengthM: length.totalM, surfaceWidthM: width.totalM, noSeries: !tiled };
      var overhangs = {};
      ['left', 'right', 'top', 'bottom'].forEach(function (side) {
        var key = { left: 'hdl', right: 'hdr', top: 'hdt', bottom: 'hdb' }[side];
        var desiredM = Math.min(Number(rules.playableLength) / 2,
          Math.max(0, number(params.get(key), defaultOverhangM / (unit === 'ft' ? 0.3048 : 0.01))) * (unit === 'ft' ? 0.3048 : 0.01));
        overhangs[side] = desiredM;
        var effectiveM = sides[side] && typeof cfg.resolveHoopOverhangGeometry === 'function'
          ? cfg.resolveHoopOverhangGeometry(desiredM, baseOverhangM).backboardInsetM : baseOverhangM;
        geometryOptions[side + 'BackboardFromEndlineM'] = effectiveM;
        if (side === primarySide) result.overhangM = desiredM;
      });
      var officialGeometry = cfg.resolveOfficialCourtGeometry(rules, geometryOptions);
      var basketballGeometry = officialGeometry && officialGeometry.features && officialGeometry.features.basketball;
      var official = basketballGeometry && basketballGeometry.official;
      var draw = primarySide && basketballGeometry && basketballGeometry.sideDraws && basketballGeometry.sideDraws[primarySide];
      if (official) {
        var officialKeyWidthM = Number(official.keyWidthOuterEdgeM);
        var officialKeyLengthM = Number(official.keyLengthFarEdgeM);
        var keyWidthM = number(params.get('kw'), officialKeyWidthM / factor) * factor;
        var keyLengthM = number(params.get('kl'), officialKeyLengthM / factor) * factor;
        var alignment = typeof global.swishcb_tiles_normalizeKeyAlignmentMode === 'function'
          ? global.swishcb_tiles_normalizeKeyAlignmentMode(params.get('kta')) : 'official';
        if (tiled && alignment === 'match') {
          keyWidthM = nearest(officialKeyWidthM, 0.01, Infinity, series).totalM;
          keyLengthM = nearest(officialKeyLengthM, 0.01, Infinity, series).totalM;
        }
        result.keyWidthM = keyWidthM;
        result.keyLengthM = Math.max(0, keyLengthM + (draw ? Number(draw.freeThrowFarEdgeFromBaselineM) - officialKeyLengthM : 0));
        result.threePointM = number(params.get('tp'), Number(official.threePointOuterRadiusM) / factor) * factor;
        var hiddenDetails = String(params.get('dv') || '').split(',');
        // Same inexpensive reach guard as ordering.hasThreePointGeometryRoom().
        // A compact floor whose farthest corner cannot reach the arc must not
        // advertise a 3-point line before the native summary is initialized.
        result.threePointVisible = ['left', 'right', 'top', 'bottom'].some(function (side) {
          if (!sides[side] || hiddenDetails.indexOf(side + 'ThreePointLine') >= 0) return false;
          var horizontal = side === 'left' || side === 'right';
          var axisLengthM = horizontal ? length.totalM : width.totalM;
          var crossWidthM = horizontal ? width.totalM : length.totalM;
          var overhangM = overhangs[side] > 0 ? overhangs[side] : 1.2;
          return Math.hypot(Math.max(0, axisLengthM - overhangM), Math.max(0, crossWidthM / 2)) + 0.001 >= result.threePointM;
        });
      }
    }
    if (tiled && global.SwishCBPricing && typeof global.SwishCBPricing.calculate === 'function') {
      result.pricing = global.SwishCBPricing.calculate({ widthM: installedWidthM, lengthM: installedLengthM,
        series: series, countryCode: countryCode });
    }
    return result;
  }

  global.SwishCBPreviewSummaryMath = { resolve: resolve };
})(typeof window !== 'undefined' ? window : globalThis);
